<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="800px"
  >
    <!--<editor-fold desc="Dialog Header">-->
    <template slot="dialogTitle">
      <span v-if="yachtEngineDirty.id">
        Edit Yacht Engine
      </span>
      <span v-else>
        Add New Yacht Engine
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Form">-->
    <v-card-text :class="{'pa-5': !$vuetify.breakpoint.xsOnly }">
      <v-text-field
        v-model="yachtEngineDirty.manufacturer"
        v-validate="'required'"
        name="manufacturer"
        label="Manufacturer"
        placeholder="e.g. YachtManufacturer d.o.o"
        outlined
        :error-messages="veeErrors.collect('manufacturer')"
      />
      <v-text-field
        v-model="yachtEngineDirty.model"
        v-validate="'required'"
        name="model"
        label="Model"
        placeholder="e.g. Model x832c"
        outlined
        :error-messages="veeErrors.collect('model')"
      />
      <v-text-field
        v-model="yachtEngineDirty.power"
        v-validate="'required'"
        type="decimal"
        name="power"
        label="Power"
        placeholder="140"
        outlined
        :error-messages="veeErrors.collect('power')"
      />
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-btn
        v-if="yachtEngineDirty.id && $can('delete', 'YachtEngine')"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Yacht Engine?"
      @confirmed="deleteYachtEngine"
    />
  </content-dialog>
</template>

<script>
  import cloneDeep from 'lodash/cloneDeep'
  import { mapState } from 'vuex'
  import YachtEngine from '@/models/YachtEngine'
  import ContentDialog from '@/components/helpers/Dialog'
  import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
  import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'

  export default {
    name: 'YachtEngineEditDialog',
    components: {
      ContentDialog,
      ConfirmDeleteDialog
    },
    mixins: [deleteConfirmationMixin],
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      yachtEngine: {
        type: Object,
        default: () => YachtEngine.emptyModel
      }
    },
    data() {
      return {
        yachtEngineDirty: cloneDeep(this.yachtEngine),
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      ...mapState('settings', ['yachtManufacturers', 'countries'])
    },
    watch: {
      yachtEngine() {
        this.yachtEngineDirty = cloneDeep(this.yachtEngine)
      }
    },
    methods: {
      async submit() {
        let validation = await this.$validator.validate()

        if (!validation) return

        this.save()
      },
      async save() {
        this.submitting = true

        const data = {
          id: this.yachtEngineDirty.id,
          manufacturer: this.yachtEngineDirty.manufacturer,
          model: this.yachtEngineDirty.model,
          power: this.yachtEngineDirty.power
        }

        try {
          let yachtEngine = await YachtEngine.api.save(data, this.yachtEngineDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Yacht Engine saved',
            'color': 'success'
          })
          this.modalVisible = false
          this.$emit(this.yachtEngineDirty.id ? 'yachtEngine:updated' : 'yachtEngine:created', yachtEngine)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
        } finally {
          this.submitting = false
        }
      },
      async deleteYachtEngine() {
        this.deleting = true

        try {
          await YachtEngine.api.destroy(this.yachtEngineDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Yacht Engine deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('yachtEngine:deleted', this.yachtEngineDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      }
    }
  }
</script>
